import type { Resource } from "@eai-toolkit/client";
import { ExpandMore } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  styled,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  useGetRoleMembersQuery,
  useGetTeamMembersQuery,
} from "../../../store/api/rolesApi";
import { useUsersWithMap } from "../../../store/hooks/useUsersWithMap";
import type { Rule } from "./data/types";

export type MembershipRef = Pick<Resource, "urn" | "fullName">;

const FADE_SLACK_PX = 8;

const MemberList = styled("ul")(({ theme }) => ({
  margin: 0,
  paddingLeft: theme.spacing(2.5),
  // A large team scrolls in place rather than pushing the attributes off the drawer.
  maxHeight: theme.spacing(30),
  overflowY: "auto",
  "& li": {
    ...theme.typography.body2,
    color: theme.palette.text.secondary,
  },
}));

// Wraps the list so the fade can sit over its bottom edge.
const ListFrame = styled("div")({
  position: "relative",
});

const BottomFade = styled("div")(({ theme }) => ({
  position: "absolute",
  left: 0,
  right: 0,
  bottom: 0,
  height: theme.spacing(4),
  // Reads as "more below" — macOS keeps scrollbars hidden until something
  // scrolls, so a clipped row would otherwise look like the end of the list.
  background: `linear-gradient(to bottom, transparent, ${theme.palette.background.paper})`,
  pointerEvents: "none",
}));

const CompactAccordion = styled(Accordion)(({ theme }) => ({
  boxShadow: "none",
  backgroundColor: "transparent",
  marginTop: theme.spacing(1.5),
  "&:before": { display: "none" },
  "& .MuiAccordionSummary-root": {
    minHeight: 0,
    padding: 0,
  },
  "& .MuiAccordionSummary-content": {
    margin: theme.spacing(0.5, 0),
  },
  "& .MuiAccordionDetails-root": {
    padding: theme.spacing(0, 0, 1),
  },
}));

/**
 * The team a grant reached the user through, when the grant's source names one.
 */
export function useGrantTeam(
  rule: Rule | null,
  userTeams: MembershipRef[],
): { teamId: string | null; teamLabel: string } {
  const source = rule?.source;
  const isTeamSource = source?.kind === "team";
  const roleId = isTeamSource && source.viaTeamRole ? source.id : null;

  // currentData, not data: `data` would keep the previous role's teams while
  // this lookup settles, resolving to the wrong team.
  const { currentData: roleTeams = [] } = useGetRoleMembersQuery(
    { roleId: roleId ?? "", type: "team" },
    { skip: !roleId },
  );

  if (!isTeamSource) return { teamId: null, teamLabel: "team" };
  if (!roleId) {
    return {
      teamId: source.id ?? null,
      teamLabel: `team: ${source.id ?? "—"}`,
    };
  }

  const userTeamKeys = new Set(
    userTeams.flatMap((team) => [team.urn, team.fullName].filter(Boolean)),
  );
  const names = roleTeams
    .filter(
      (team) => userTeamKeys.has(team.urn) || userTeamKeys.has(team.fullName),
    )
    .map((team) => team.fullName ?? team.id);

  if (names.length === 1) {
    return { teamId: names[0], teamLabel: `team: ${names[0]}` };
  }
  return {
    teamId: null,
    teamLabel: names.length > 1 ? `team: ${names.join(" or ")}` : "team",
  };
}

type Props = {
  // Team full name, or null when the grant isn't a team grant or the team
  // behind a role couldn't be pinned down.
  teamId: string | null;
};

const GrantTeamMembers = ({ teamId }: Props) => {
  const [expanded, setExpanded] = useState(false);
  // Whether rows remain below the fold — macOS hides scrollbars, so a clipped
  // list needs an explicit cue (see BottomFade).
  const [hasMoreBelow, setHasMoreBelow] = useState(false);
  const listRef = useRef<HTMLUListElement | null>(null);
  // Re-collapse when the drawer switches rules so a new team's roster isn't
  // fetched unasked.
  useEffect(() => setExpanded(false), [teamId]);

  // currentData, not data: `data` would flash the previous team's count while
  // this one is in flight.
  const { currentData: summary, isError } = useGetTeamMembersQuery(
    { teamId: teamId ?? "", type: "user" },
    { skip: !teamId },
  );

  // The first read of useGetTeamMembersQuery is count only - then read again
  // to get the full list if the section is expanded
  const { data: full, isFetching: isFetchingMembers } = useGetTeamMembersQuery(
    { teamId: teamId ?? "", type: "user", all: true },
    { skip: !teamId || !expanded },
  );
  const updateFade = useCallback((node: HTMLUListElement) => {
    setHasMoreBelow(
      node.scrollHeight - node.scrollTop - node.clientHeight >= FADE_SLACK_PX,
    );
  }, []);

  // Measured, not derived from the count — what fits depends on row heights.
  const attachList = useCallback(
    (node: HTMLUListElement | null) => {
      listRef.current = node;
      if (node) updateFade(node);
    },
    [updateFade],
  );

  const handleScroll = useCallback(() => {
    if (listRef.current) updateFade(listRef.current);
  }, [updateFade]);

  // Member rows carry no email; the directory is already loaded for the
  // view-as picker, so using it here to lookup doesn't add any extra requests.
  const { usersMap } = useUsersWithMap();

  // Listing members needs `team:member:get`, which many viewers lack — on
  // failure the section is simply absent.
  if (!teamId || isError) return null;

  if (summary === undefined) return null;
  const total = summary.total;
  if (total === 0) return null;

  const members = full?.members ?? [];
  const labelled = members
    .map((member) => ({
      id: member.id,
      label:
        member.fullName ||
        member.name ||
        usersMap.get(member.id)?.mail ||
        member.id,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <CompactAccordion
      disableGutters
      expanded={expanded}
      onChange={(_event, isExpanded) => setExpanded(isExpanded)}
    >
      <AccordionSummary expandIcon={<ExpandMore fontSize="small" />}>
        <Typography variant="body2">
          {total === 1
            ? "1 member holds this grant via the team"
            : `${total} members hold this grant via the team`}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {isFetchingMembers ? (
          <Typography variant="body2" color="text.secondary">
            Loading team members…
          </Typography>
        ) : (
          <ListFrame>
            <MemberList ref={attachList} onScroll={handleScroll}>
              {labelled.map((member) => (
                <li key={member.id}>{member.label}</li>
              ))}
            </MemberList>
            {hasMoreBelow && <BottomFade data-testid="member-list-fade" />}
          </ListFrame>
        )}
      </AccordionDetails>
    </CompactAccordion>
  );
};

export default GrantTeamMembers;
